import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { Image, LoaderCircle, RotateCcw, Save, Upload } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSiteSettings } from '../hooks/useSiteSettings'

type BackgroundKey = 'heroBackgroundUrl' | 'pageBackgroundUrl'

const AppearanceAdminModule = () => {
  const { settings, refreshSettings } = useSiteSettings()
  const [form, setForm] = useState(settings)
  const [uploading, setUploading] = useState<BackgroundKey | null>(null)
  const [saving, setSaving] = useState(false)
  const heroInputRef = useRef<HTMLInputElement>(null)
  const pageInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => setForm(settings), [settings])

  const deleteOrphan = async (imagePath: string | null) => {
    if (!imagePath) return
    try {
      await axios.delete('/api/upload', { data: { path: imagePath } })
    } catch (error) {
      if (!axios.isAxiosError(error) || ![404, 409].includes(error.response?.status ?? 0)) {
        console.error('Impossible de nettoyer le fond temporaire :', error)
      }
    }
  }

  const uploadBackground = async (file: File, key: BackgroundKey) => {
    const data = new FormData()
    data.append('backgroundImage', file)
    setUploading(key)
    try {
      const response = await axios.post<{ imageUrl: string }>('/api/upload/background', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (form[key] && form[key] !== settings[key]) await deleteOrphan(form[key])
      setForm((current) => ({ ...current, [key]: response.data.imageUrl }))
      toast.success('Image prête à être enregistrée')
    } catch (error) {
      console.error("Erreur lors de l'upload du fond :", error)
      toast.error("L'image n'a pas pu être envoyée")
    } finally {
      setUploading(null)
    }
  }

  const save = async () => {
    setSaving(true)
    try {
      await axios.put('/api/site-settings', form)
      await refreshSettings()
      toast.success('Nouvelle ambiance publiée')
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de l'apparence :", error)
      toast.error("L'apparence n'a pas pu être enregistrée")
    } finally {
      setSaving(false)
    }
  }

  const backgrounds: Array<{
    key: BackgroundKey
    title: string
    description: string
    inputRef: React.RefObject<HTMLInputElement | null>
  }> = [
    {
      key: 'heroBackgroundUrl',
      title: "Bandeau de la page d'accueil",
      description: 'Format paysage recommandé, au moins 1 920 × 1 080 px.',
      inputRef: heroInputRef,
    },
    {
      key: 'pageBackgroundUrl',
      title: 'Ambiance générale des pages',
      description: 'Une image sombre ou peu chargée garantit une bonne lisibilité.',
      inputRef: pageInputRef,
    },
  ]

  return (
    <div className="space-y-6">
      <div className="max-w-3xl">
        <p className="eyebrow">Direction artistique</p>
        <h3 className="text-2xl font-bold text-gray-900">Images de fond</h3>
        <p className="mt-2 text-gray-600">
          Les voiles verts et les contrastes sont appliqués automatiquement pour préserver la lisibilité.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {backgrounds.map(({ key, title, description, inputRef }) => (
          <article key={key} className="card overflow-hidden !p-0">
            <div
              className="relative flex aspect-video items-center justify-center overflow-hidden bg-emerald-950"
              style={form[key] ? { backgroundImage: `linear-gradient(135deg, rgb(4 51 28 / .32), rgb(3 20 13 / .72)), url(${form[key]})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
            >
              {!form[key] && <Image className="h-12 w-12 text-emerald-200/60" aria-hidden="true" />}
              <span className="absolute bottom-4 left-4 rounded-full bg-emerald-950/65 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md">
                Aperçu
              </span>
            </div>
            <div className="space-y-4 p-5">
              <div>
                <h4 className="font-bold text-gray-900">{title}</h4>
                <p className="mt-1 text-sm text-gray-600">{description}</p>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file) void uploadBackground(file, key)
                  event.target.value = ''
                }}
              />
              <div className="flex flex-wrap gap-3">
                <button type="button" className="btn-primary" onClick={() => inputRef.current?.click()} disabled={uploading !== null}>
                  {uploading === key ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Choisir une image
                </button>
                {form[key] && (
                  <button type="button" className="btn-secondary" onClick={() => {
                    if (form[key] !== settings[key]) void deleteOrphan(form[key])
                    setForm((current) => ({ ...current, [key]: null }))
                  }}>
                    <RotateCcw className="h-4 w-4" /> Retirer
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="flex justify-end border-t border-emerald-950/10 pt-5">
        <button type="button" className="btn-primary" onClick={() => void save()} disabled={saving || uploading !== null}>
          {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Publier l'apparence
        </button>
      </div>
    </div>
  )
}

export default AppearanceAdminModule
